import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { IndicadoresService, TiendaCatalogoApi } from '../../indicadores/services/indicadores.service';
import { useEsMovil } from '../../indicadores/hooks/useEsMovil';
import { RoleService } from '../../layouts/services/role.service';
import { StorageService } from '../../auth/services/storage.service';
import { UserRole } from '../../auth/types/auth.types';
import { DispositivosApiError, DispositivosService, FiltroDispositivos } from '../services/dispositivos.service';
import { Dispositivo, DIAS_OPCIONES, ListaDispositivos } from '../types/dispositivos.types';
import {
  chipsResumen,
  codigosVisibles,
  ESPERA_CONFIRMACION_MS,
  esGerente,
  filtrar,
  nombreEquipo,
  ordenar,
  puedeCerrarSesion,
  puedeVerDispositivos,
  segundosDesdeArranque,
  SonarLocal,
} from '../utils/dispositivos';
import { Acciones, Codigo, Cuenta, Estado, Etiqueta, Vendedor, VersionApp, Visto } from '../components/PartesDispositivo';
import { AyudaSonarDialog } from '../components/AyudaSonar';
import { CerrarSesionDialog } from '../components/CerrarSesionDialog';
import '../styles/dispositivos.css';

/**
 * «Dispositivos de tienda» (spec 2026-10-13, §7.3).
 *
 * POR QUÉ EXISTE
 * El 10-oct, en TRE 74 Tezontle, se perdió una handheld y no hubo forma de
 * saber cuántos equipos tenía la tienda ni de sacar a ése de la sesión: las
 * cinco entran con la misma cuenta `branch_office` y el servidor no ve nada
 * que las distinga. La app nueva le da a cada equipo un código propio
 * («Equipo 7F3A»); aquí TI lo encuentra, lo hace sonar y cierra su sesión sin
 * tocar a los demás.
 *
 * Quién ve qué (decisión del usuario, 13-oct):
 * - superadmin y admin: cualquier tienda, y «Cerrar sesión» / «Reactivar»;
 * - el gerente (`manager`): SÓLO su tienda —la API la fuerza— y sólo ver,
 *   etiquetar y hacer sonar;
 * - las cuentas `indicadores`: nada.
 */

/**
 * Hasta este ancho (inclusive) cada equipo es una TARJETA; por encima, tabla.
 *
 * La regla del proyecto: más de 3 columnas en teléfono → tarjeta por fila,
 * nunca *scroll* horizontal (lección 2026-08-29). La tabla tiene 6 columnas.
 * Medido en Chromium con datos realistas (correos largos, etiqueta larga):
 *
 *     1025 px  ->  37 px de desborde
 *     1100 px  ->   0, pero apretada
 *     1153 px  ->   0 y se lee bien
 *
 * Es el mismo número que `CORTE_MOVIL` de indicadores, por otra medición.
 * Entre 700 y 1152 px las tarjetas van de dos en dos.
 */
export const CORTE_TARJETAS = 1152;

/** Refresco automático, sólo con la pestaña visible (§7.3). */
export const REFRESCO_MS = 30_000;

/** Tras «Hacer sonar», cuándo volver a leer la lista para ver si el equipo confirmó. */
const RELECTURAS_SONAR_MS = [4_000, 12_000, ESPERA_CONFIRMACION_MS + 1_000];

const OPCIONES_DIAS = DIAS_OPCIONES.map((d) => ({ label: `${d} días`, value: d }));

const etiquetaTienda = (t: Pick<TiendaCatalogoApi, 'chain' | 'num' | 'tienda'>) => `${t.chain} ${t.num} · ${t.tienda}`;

export const DispositivosScreen: React.FC = () => {
  const rol = RoleService.getUserRole();
  if (!puedeVerDispositivos(rol)) {
    // Por enlace directo: la API respondería 403, pero ni se le pregunta.
    return (
      <div className="disp">
        <h2 className="disp-titulo">Dispositivos de tienda</h2>
        <Message
          severity="warn"
          text="Esta pantalla es sólo para TI y para los gerentes de tienda."
          className="disp-mensaje-ancho"
        />
      </div>
    );
  }
  return <PantallaDispositivos rol={rol as UserRole} />;
};

const PantallaDispositivos: React.FC<{ rol: UserRole }> = ({ rol }) => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const tarjetas = useEsMovil(CORTE_TARJETAS);
  const gerente = esGerente(rol);
  const puedeCerrar = puedeCerrarSesion(rol);

  const [tiendas, setTiendas] = useState<TiendaCatalogoApi[]>([]);
  const [errorCatalogo, setErrorCatalogo] = useState<string | null>(null);
  const [cadena, setCadena] = useState<string | null>(null);
  const [tienda, setTienda] = useState<TiendaCatalogoApi | null>(null);
  const [dias, setDias] = useState<number>(7);
  const [consulta, setConsulta] = useState('');

  const [lista, setLista] = useState<ListaDispositivos | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorRefresco, setErrorRefresco] = useState<string | null>(null);
  const [actualizado, setActualizado] = useState<Date | null>(null);

  const [sonar, setSonar] = useState<Record<string, SonarLocal>>({});
  const [cerrando, setCerrando] = useState<{ d: Dispositivo; codigo: string } | null>(null);
  const [ayuda, setAyuda] = useState(false);
  const [pendienteContrasena, setPendienteContrasena] = useState<{ cuenta: string; nombre: string } | null>(null);

  const avisar = (severity: 'success' | 'warn' | 'error', summary: string, detail?: string) =>
    toast.current?.show({ severity, summary, detail, life: severity === 'success' ? 4000 : 9000 });

  /* ---------------------------------------------------------- catálogo -- */

  const leerCatalogo = useCallback(() => {
    setErrorCatalogo(null);
    IndicadoresService.getCatalogoTiendas()
      .then((c) => setTiendas(c.tiendas ?? []))
      .catch((e) => setErrorCatalogo(e instanceof Error ? e.message : 'No se pudo leer el catálogo de tiendas.'));
  }, []);

  useEffect(() => {
    leerCatalogo();
  }, [leerCatalogo]);

  const cadenas = useMemo(() => Array.from(new Set(tiendas.map((t) => t.chain))).sort(), [tiendas]);
  const opcionesTienda = useMemo(
    () =>
      tiendas
        .filter((t) => !cadena || t.chain === cadena)
        .sort((a, b) => a.chain.localeCompare(b.chain) || a.num - b.num)
        .map((t) => ({ label: etiquetaTienda(t), value: t })),
    [tiendas, cadena],
  );

  /** El gerente no elige: es su tienda, la que fuerza la API. Aquí sólo se nombra. */
  const tiendaGerente = useMemo(() => {
    if (!gerente) return null;
    const u = StorageService.getUser();
    const chain = u?.chainId ?? null;
    const num = u?.branchOffice ?? null;
    const t = tiendas.find((x) => x.chain === chain && x.num === num);
    if (t) return etiquetaTienda(t);
    return chain && num !== null ? `${chain} ${num}` : null;
  }, [gerente, tiendas]);

  /* ------------------------------------------------------------- lista -- */

  const filtro: FiltroDispositivos | null = useMemo(() => {
    if (gerente) return { dias };
    return tienda ? { cadena: tienda.chain, sucursal: tienda.num, dias } : null;
  }, [gerente, tienda, dias]);

  // Una respuesta vieja (de la tienda anterior) no pisa a la nueva.
  const pedido = useRef(0);
  const filtroActual = useRef(filtro);
  filtroActual.current = filtro;

  const cargar = useCallback(async (silencioso: boolean) => {
    const f = filtroActual.current;
    if (!f) return;
    const n = ++pedido.current;
    if (!silencioso) {
      setCargando(true);
      setError(null);
    }
    try {
      const r = await DispositivosService.listar(f);
      if (n !== pedido.current) return;
      setLista(r);
      setError(null);
      setErrorRefresco(null);
      setActualizado(new Date());
    } catch (e) {
      if (n !== pedido.current) return;
      const msg = e instanceof Error ? e.message : 'No se pudo leer la lista de equipos.';
      if (silencioso) {
        setErrorRefresco(msg);
      } else {
        setError(msg);
        setLista(null);
      }
    } finally {
      if (n === pedido.current) setCargando(false);
    }
  }, []);

  useEffect(() => {
    setLista(null);
    setSonar({});
    setErrorRefresco(null);
    if (filtro) void cargar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro?.cadena, filtro?.sucursal, filtro?.dias, cargar]);

  // Cada 30 s y al volver a la pestaña; nunca con la pestaña oculta.
  const ultimoRefresco = useRef(0);
  useEffect(() => {
    if (actualizado) ultimoRefresco.current = actualizado.getTime();
  }, [actualizado]);
  useEffect(() => {
    const tic = window.setInterval(() => {
      if (!document.hidden) void cargar(true);
    }, REFRESCO_MS);
    const alVolver = () => {
      if (!document.hidden && Date.now() - ultimoRefresco.current >= REFRESCO_MS) void cargar(true);
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.clearInterval(tic);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [cargar]);

  const relecturas = useRef<number[]>([]);
  useEffect(
    () => () => {
      relecturas.current.forEach((t) => window.clearTimeout(t));
    },
    [],
  );

  /* ---------------------------------------------------------- acciones -- */

  const hacerSonar = async (d: Dispositivo) => {
    setSonar((s) => ({ ...s, [d.id]: { tipo: 'enviando' } }));
    try {
      const r = await DispositivosService.sonar(d.id);
      if (!r.entregado) {
        setSonar((s) => ({ ...s, [d.id]: { tipo: 'no_entregado' } }));
        return;
      }
      setSonar((s) => ({
        ...s,
        [d.id]: { tipo: 'enviado', sockets: r.sockets, enviadoEn: Date.now(), previo: d.ultimoSonar?.en ?? null },
      }));
      // El equipo confirma (`sono`, `bloqueado`, `sin_audio`) por su lado: se
      // vuelve a leer la lista unas veces para enseñarlo.
      RELECTURAS_SONAR_MS.forEach((ms) => relecturas.current.push(window.setTimeout(() => void cargar(true), ms)));
    } catch (e) {
      const limite = e instanceof DispositivosApiError && e.esLimite;
      const mensaje = e instanceof Error ? e.message : 'No se pudo hacer sonar el equipo.';
      setSonar((s) => ({ ...s, [d.id]: { tipo: 'error', mensaje, limite } }));
    }
  };

  const etiquetar = async (d: Dispositivo, etiqueta: string | null) => {
    await DispositivosService.etiquetar(d.id, etiqueta);
    setLista((l) =>
      l ? { ...l, dispositivos: l.dispositivos.map((x) => (x.id === d.id ? { ...x, etiqueta } : x)) } : l,
    );
  };

  const reactivar = (d: Dispositivo, codigo: string) => {
    const nombre = nombreEquipo(codigo);
    confirmDialog({
      header: `Reactivar ${nombre}`,
      message: `${nombre} podrá volver a iniciar sesión. Hazlo sólo si el equipo ya está de vuelta y en manos de la tienda.`,
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sí, reactivar',
      rejectLabel: 'Cancelar',
      className: 'disp-overlay',
      accept: async () => {
        try {
          await DispositivosService.reactivar(d.id);
          avisar('success', `${nombre} reactivado`, 'Ya puede volver a iniciar sesión.');
          void cargar(true);
        } catch (e) {
          avisar('error', 'No se pudo reactivar', e instanceof Error ? e.message : undefined);
        }
      },
    });
  };

  /* ------------------------------------------------------------- vista -- */

  const ahora = Date.now();
  const todos = useMemo(() => ordenar(lista?.dispositivos ?? []), [lista]);
  const codigos = useMemo(() => codigosVisibles(todos), [todos]);
  const visibles = useMemo(() => filtrar(todos, codigos, consulta), [todos, codigos, consulta]);
  const arranque = segundosDesdeArranque(lista?.apiIniciadaEn, ahora);

  const partes = (d: Dispositivo) => {
    const codigo = codigos[d.id] ?? d.codigo;
    const nombre = nombreEquipo(codigo);
    return {
      codigo,
      nombre,
      acciones: (
        <Acciones
          d={d}
          nombre={nombre}
          ahora={ahora}
          puedeCerrar={puedeCerrar}
          sonar={sonar[d.id]}
          onSonar={() => void hacerSonar(d)}
          onAyuda={() => setAyuda(true)}
          onCerrarSesion={() => setCerrando({ d, codigo })}
          onReactivar={() => reactivar(d, codigo)}
        />
      ),
    };
  };

  const tabla = (
    <div className="disp-tabla-marco">
      <table className="disp-tabla" data-testid="disp-tabla">
        <thead>
          <tr>
            <th>Equipo</th>
            <th>Estado</th>
            <th>Cuenta</th>
            <th>Último vendedor</th>
            <th>App</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {visibles.map((d) => {
            const p = partes(d);
            return (
              <tr key={d.id} data-testid="disp-fila" className={d.estado === 'revocado' ? 'disp-revocado' : undefined}>
                <td className="disp-col-equipo">
                  <Codigo codigo={p.codigo} nuevo={d.nuevo} />
                  <Etiqueta d={d} nombre={p.nombre} onGuardar={(e) => etiquetar(d, e)} />
                </td>
                <td className="disp-col-estado">
                  {/* «Visto» va bajo el estado: en la tabla, una columna propia
                      aplastaba a las demás entre 1100 y 1280 px (medido). */}
                  <Estado d={d} ahora={ahora} />
                  <div className="disp-visto-en-estado">
                    <Visto d={d} ahora={ahora} />
                  </div>
                </td>
                <td>
                  <Cuenta d={d} />
                </td>
                <td>
                  <Vendedor d={d} ahora={ahora} />
                </td>
                <td>
                  <VersionApp d={d} />
                </td>
                <td className="disp-col-acciones">{p.acciones}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const listaTarjetas = (
    <ul className="disp-tarjetas" data-testid="disp-tarjetas">
      {visibles.map((d) => {
        const p = partes(d);
        return (
          <li key={d.id} className={`disp-tarjeta${d.estado === 'revocado' ? ' disp-revocado' : ''}`} data-testid="disp-tarjeta">
            <div className="disp-tarjeta-cab">
              <Codigo codigo={p.codigo} nuevo={d.nuevo} />
              <Estado d={d} ahora={ahora} />
            </div>
            <Etiqueta d={d} nombre={p.nombre} onGuardar={(e) => etiquetar(d, e)} />
            <dl className="disp-datos">
              <dt>Cuenta</dt>
              <dd>
                <Cuenta d={d} />
              </dd>
              <dt>Último vendedor</dt>
              <dd>
                <Vendedor d={d} ahora={ahora} />
              </dd>
              <dt>Visto</dt>
              <dd>
                <Visto d={d} ahora={ahora} />
              </dd>
              <dt>App</dt>
              <dd>
                <VersionApp d={d} />
              </dd>
            </dl>
            {p.acciones}
          </li>
        );
      })}
    </ul>
  );

  let cuerpo: React.ReactNode;
  if (!filtro) {
    cuerpo = <div className="disp-vacio">Elige una tienda para ver sus equipos.</div>;
  } else if (error) {
    cuerpo = (
      <div className="disp-error-bloque">
        <Message severity="error" text={error} className="disp-mensaje-ancho" />
        <Button label="Reintentar" icon="pi pi-refresh" className="p-button-sm p-button-outlined" onClick={() => void cargar(false)} />
      </div>
    );
  } else if (!lista) {
    cuerpo = (
      <div className="disp-vacio" role="status">
        <i className="pi pi-spin pi-spinner" aria-hidden="true" /> Cargando equipos…
      </div>
    );
  } else if (todos.length === 0) {
    cuerpo = (
      <div className="disp-vacio" data-testid="disp-sin-equipos">
        <strong>Aún no hay equipos registrados en esta tienda: aparecen cuando la app nueva se abre.</strong>
        {dias < 90 && (
          <div className="disp-tenue">
            La lista sólo trae los vistos en los últimos {dias} días; prueba con un periodo más largo si la tienda ya
            usaba la app nueva.
          </div>
        )}
      </div>
    );
  } else if (visibles.length === 0) {
    cuerpo = <div className="disp-vacio">Ningún equipo coincide con «{consulta.trim()}».</div>;
  } else {
    cuerpo = tarjetas ? listaTarjetas : tabla;
  }

  return (
    <div className={`disp${tarjetas ? ' disp--tarjetas' : ''}`}>
      <Toast ref={toast} />
      <ConfirmDialog />

      <h2 className="disp-titulo">Dispositivos de tienda</h2>
      <p className="disp-bajada">
        Cada equipo que abre la app nueva aparece aquí con su código («Equipo 7F3A»), el mismo que se ve en la
        pantalla de inicio de sesión del equipo. Sirve para saber cuántos equipos tiene una tienda, hacer sonar uno que
        no aparece y {puedeCerrar ? 'cerrar la sesión de uno perdido sin afectar a los demás' : 'avisar a TI si se pierde uno'}.
      </p>

      <div className="disp-barra">
        {gerente ? (
          <div className="disp-campo">
            <span className="disp-campo-etiqueta">Tienda</span>
            <div className="disp-tienda-fija" data-testid="tienda-gerente">
              {tiendaGerente ?? 'Tu tienda'}
            </div>
          </div>
        ) : (
          <>
            <div className="disp-campo disp-campo-cadena">
              <label htmlFor="disp-cadena" className="disp-campo-etiqueta">
                Cadena
              </label>
              <Dropdown
                inputId="disp-cadena"
                value={cadena}
                options={[{ label: 'Todas', value: null }, ...cadenas.map((c) => ({ label: c, value: c }))]}
                onChange={(e) => {
                  setCadena(e.value);
                  if (e.value && tienda && tienda.chain !== e.value) setTienda(null);
                }}
                placeholder="Todas"
                className="w-full"
              />
            </div>
            <div className="disp-campo disp-campo-tienda">
              <label htmlFor="disp-tienda" className="disp-campo-etiqueta">
                Tienda
              </label>
              <Dropdown
                inputId="disp-tienda"
                value={tienda}
                options={opcionesTienda}
                onChange={(e) => setTienda(e.value)}
                filter
                filterPlaceholder="Número o nombre"
                placeholder={tiendas.length ? 'Elige una tienda' : 'Cargando tiendas…'}
                emptyMessage="Sin tiendas"
                emptyFilterMessage="Ninguna tienda coincide"
                className="w-full"
              />
            </div>
          </>
        )}
        <div className="disp-campo">
          <span className="disp-campo-etiqueta" id="disp-dias-etiqueta">
            Vistos en los últimos
          </span>
          <SelectButton
            value={dias}
            options={OPCIONES_DIAS}
            onChange={(e) => {
              if (e.value) setDias(e.value);
            }}
            allowEmpty={false}
            aria-labelledby="disp-dias-etiqueta"
            className="disp-dias"
          />
        </div>
        <div className="disp-campo disp-campo-buscar">
          <label htmlFor="disp-buscar" className="disp-campo-etiqueta">
            Buscar
          </label>
          <InputText
            id="disp-buscar"
            value={consulta}
            onChange={(e) => setConsulta(e.target.value)}
            placeholder="Código, etiqueta o vendedor"
            className="w-full"
          />
        </div>
        {filtro && (
          <div className="disp-actualizado">
            {actualizado && (
              <span>
                actualizado a las <b>{actualizado.toLocaleTimeString('es-MX')}</b>
              </span>
            )}
            <Button
              label="Actualizar"
              icon="pi pi-refresh"
              className="p-button-sm p-button-text"
              onClick={() => void cargar(lista !== null)}
              disabled={cargando}
            />
          </div>
        )}
      </div>

      {errorCatalogo && !gerente && (
        <div className="disp-error-bloque">
          <Message severity="error" text={`No se pudo leer el catálogo de tiendas: ${errorCatalogo}`} className="disp-mensaje-ancho" />
          <Button label="Reintentar" icon="pi pi-refresh" className="p-button-sm p-button-outlined" onClick={leerCatalogo} />
        </div>
      )}

      {pendienteContrasena && (
        <Message
          severity="warn"
          className="disp-mensaje-ancho disp-pendiente"
          content={
            <div className="disp-pendiente-contenido">
              <span>
                Se cerró la sesión de <strong>{pendienteContrasena.nombre}</strong>. Falta cambiar la contraseña de{' '}
                <strong>{pendienteContrasena.cuenta}</strong> en Usuarios y dársela al gerente.
              </span>
              <span className="disp-pendiente-botones">
                <Button label="Ir a Usuarios" className="p-button-sm" onClick={() => navigate('/users')} />
                <Button
                  label="Ya la cambié"
                  className="p-button-sm p-button-text"
                  onClick={() => setPendienteContrasena(null)}
                />
              </span>
            </div>
          }
        />
      )}

      {lista && (
        <>
          <ul className="disp-chips" aria-label="Resumen" data-testid="disp-resumen">
            {chipsResumen(lista.resumen, dias).map((c) => (
              <li key={c.clave} className={`disp-chip disp-chip-${c.tono}`}>
                {c.texto}
              </li>
            ))}
          </ul>
          {arranque !== null && (
            <Message
              severity="info"
              className="disp-mensaje-ancho"
              text={`La API se reinició hace ${arranque} s: los equipos se están volviendo a conectar, así que «en pantalla» y «conexión abierta» pueden marcar de menos durante un minuto.`}
            />
          )}
          {errorRefresco && (
            <Message
              severity="warn"
              className="disp-mensaje-ancho"
              text={`No se pudo actualizar (${errorRefresco}). Lo que se ve es de las ${actualizado?.toLocaleTimeString('es-MX') ?? '—'}.`}
            />
          )}
        </>
      )}

      {cuerpo}

      <AyudaSonarDialog visible={ayuda} onHide={() => setAyuda(false)} />
      <CerrarSesionDialog
        dispositivo={cerrando?.d ?? null}
        codigo={cerrando?.codigo ?? ''}
        onHide={() => setCerrando(null)}
        onCerrado={() => {
          if (cerrando) {
            setPendienteContrasena({ cuenta: cerrando.d.cuenta?.email ?? 'la cuenta', nombre: nombreEquipo(cerrando.codigo) });
          }
          void cargar(true);
        }}
        onIrAUsuarios={() => {
          setCerrando(null);
          navigate('/users');
        }}
      />
    </div>
  );
};
